import type { DragEndEvent } from '@dnd-kit/core';
import type { IStackProps } from '@sixui/ui';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { delay } from '@olivierpascal/helpers';
import { Stack } from '@sixui/ui';

import type { IAny, IMaybeAsync } from '~/helpers/types';

type ISortableStackItem = {
  id: string;
  pending?: boolean;
};

export type ISortableStackRenderProps = ISortableStackItem & {
  index: number;
  disabled?: boolean;
};

export type ISortableStackProps = IStackProps & {
  value?: Array<string>;
  onChange?: (value: Array<string>) => IMaybeAsync<IAny>;
  minDelay?: number;
  disabled?: boolean;
  start?: React.ReactNode;
  end?: React.ReactNode;
  itemRenderer: (props: ISortableStackRenderProps) => React.ReactNode;
};

export const SortableStack = forwardRef<HTMLDivElement, ISortableStackProps>(
  function SortableStack(props, forwardedRef) {
    const {
      value = [],
      onChange,
      minDelay,
      disabled,
      start,
      end,
      itemRenderer,
      ...other
    } = props;
    const [items, setItems] = useState<Array<ISortableStackItem>>(
      value.map((id) => ({ id, pending: false })),
    );
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      }),
    );
    const pendingChangesCountRef = useRef(0);
    const lastChangeRef = useRef<IMaybeAsync<IAny>>(undefined);
    const lastValidItemsRef = useRef(items);

    useEffect(() => {
      setItems((items) =>
        value.map((id) => ({
          id,
          pending: items.some((item) => item.id === id && item.pending),
        })),
      );
    }, [value]);

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
          // No change.
          return;
        }

        const ids = items.map(({ id }) => id);
        const oldIndex = ids.indexOf(active.id as string);
        const newIndex = ids.indexOf(over.id as string);
        if (oldIndex < 0 || newIndex < 0) {
          // Invalid state.
          return;
        }

        pendingChangesCountRef.current++;

        const reorderedItems = arrayMove(items, oldIndex, newIndex);
        const reorderedIds = reorderedItems.map(({ id }) => id);
        setItems(
          reorderedItems.map((item) => ({
            ...item,
            pending: item.id === active.id ? true : item.pending,
          })),
        );

        lastChangeRef.current = Promise.resolve(lastChangeRef.current)
          .then(() =>
            Promise.all([
              onChange?.(reorderedIds),
              minDelay ? delay(minDelay) : undefined,
            ]),
          )
          .then(() => {
            lastValidItemsRef.current = reorderedItems;
          })
          .catch(() =>
            setItems(
              reorderedItems.map((item) => ({
                ...item,
                pending: false,
              })),
            ),
          )
          .finally(() => {
            pendingChangesCountRef.current--;
            if (pendingChangesCountRef.current <= 0) {
              setItems(
                lastValidItemsRef.current.map((item) => ({
                  ...item,
                  pending: false,
                })),
              );
            }
          });
      },
      [onChange, items, minDelay],
    );

    return (
      <Stack {...other} ref={forwardedRef}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[
            other.horizontal
              ? restrictToHorizontalAxis
              : restrictToVerticalAxis,
          ]}
        >
          {start}
          <SortableContext items={items}>
            {items.map((item, index) =>
              itemRenderer({ ...item, index, disabled }),
            )}
          </SortableContext>
          {end}
        </DndContext>
      </Stack>
    );
  },
);
